import React, { useCallback, useEffect, useState } from "react";
import { Button, Tag } from "../../../../../design-system";
import mediaOps, { AccountSummary, OperationResult, ReviewView } from "../../../../../database/mediaOps/mediaOpsApi";
import ProductionPlanningSummary from "../production/ProductionPlanningSummary";
import type { ProductionBrief, ContentReference } from "../../../../../database/mediaOps/contentApi";
import JobProgress from "./JobProgress";
import ScheduleDialog, { ScheduleTarget } from "./ScheduleDialog";
import { useMutation } from "./hooks";
import { modeLabel } from "./stateLabels";
import {
  Banner,
  ErrorLine,
  FieldLabel,
  FormGrid,
  Grow,
  Hint,
  ListRow,
  Mono,
  Panel,
  PanelHead,
  ResultNotice,
  Row,
  Select,
  Small,
  TextArea,
  TextInput,
  Thumb,
  ThumbBox,
} from "./ui";

// One project: immutable revisions, revise, render, submit for review, the
// per-target review panel showing exactly what will be delivered, approval
// and scheduling. Every step is a mediaOps call; nothing here decides policy.

type Revision = {
  id: string;
  format: string;
  identityId: string;
  assets: { assetId: string; role: string }[];
  outputs?: { assetId?: string; storagePath: string; type: string; order: number }[];
  captionsByAccount: Record<string, string>;
  renderSpec?: { contentPlanning?: { productionBrief?: ProductionBrief; references?: ContentReference[] } };
  baseRevisionId: string | null;
  contentHash?: string;
  createdAt: string;
  policyFindings?: Record<string, { code: string; message: string; retryable: boolean; requiredAction?: string }[]>;
};

type ProjectData = {
  projectId: string;
  studio: { title?: string; format: string; identityId: string; currentRevisionId: string; version: number; targets: { accountId: string; deliveryMode: string }[] };
  isCurrent: boolean;
  revision: Revision;
  reviews: { reviewId: string; status: string; version: number; targets: { accountId: string; deliveryMode: string }[]; approvals: Record<string, string> }[];
  approvals: { byStatus: Record<string, number>; activeForRevision: { approvalId: string; accountId: string; deliveryMode: string }[] };
};

/** Field-level differences between two revisions, for the history view. */
export const revisionDiff = (prev: Revision | null, next: Revision): string[] => {
  if (!prev) return ["first revision"];
  const out: string[] = [];
  if (prev.format !== next.format) out.push(`format ${prev.format} to ${next.format}`);
  if (prev.identityId !== next.identityId) out.push("identity");
  if (JSON.stringify(prev.renderSpec?.contentPlanning) !== JSON.stringify(next.renderSpec?.contentPlanning)) out.push("production brief or references");
  const a = (prev.assets || []).map((x) => x.assetId).join(",");
  const b = (next.assets || []).map((x) => x.assetId).join(",");
  if (a !== b) {
    const sameSet = a.split(",").sort().join(",") === b.split(",").sort().join(",");
    out.push(sameSet ? "media order" : "media");
  }
  const oa = (prev.outputs || []).map((x) => x.storagePath).join(",");
  const ob = (next.outputs || []).map((x) => x.storagePath).join(",");
  if (oa !== ob) out.push("rendered outputs");
  const accounts = new Set([...Object.keys(prev.captionsByAccount || {}), ...Object.keys(next.captionsByAccount || {})]);
  accounts.forEach((acc) => {
    if ((prev.captionsByAccount || {})[acc] !== (next.captionsByAccount || {})[acc]) out.push(`caption for ${acc}`);
  });
  return out.length ? out : ["no content change"];
};

export const ProjectDetail: React.FC<{ communityId: string; projectId: string }> = ({ communityId, projectId }) => {
  const [project, setProject] = useState<ProjectData | null>(null);
  const [loadResult, setLoadResult] = useState<OperationResult | null>(null);
  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [history, setHistory] = useState<Revision[]>([]);
  const [staleNotice, setStaleNotice] = useState("");
  const [renderJobId, setRenderJobId] = useState<string | null>(null);
  const [openReviewId, setOpenReviewId] = useState<string | null>(null);
  const [scheduling, setScheduling] = useState<ScheduleTarget | null>(null);

  const load = useCallback(async () => {
    const r = await mediaOps.getProject(communityId, projectId);
    setLoadResult(r);
    if (r.status === "blocked") return;
    const data = r.data as ProjectData;
    setProject(data);
    // Walk the base chain for history, at most 10 back.
    const chain: Revision[] = [data.revision];
    let base = data.revision.baseRevisionId;
    for (let i = 0; base && i < 10; i++) {
      const prev = await mediaOps.getProject(communityId, projectId, base);
      if (prev.status === "blocked" || !prev.data?.revision) break;
      chain.push(prev.data.revision as Revision);
      base = (prev.data.revision as Revision).baseRevisionId;
    }
    setHistory(chain);
  }, [communityId, projectId]);

  useEffect(() => {
    load();
    mediaOps.listAccounts(communityId).then((r) => setAccounts(r.data?.accounts || []));
  }, [communityId, load]);

  if (!project) {
    return loadResult ? <ResultNotice result={loadResult} /> : <Small>Loading project.</Small>;
  }

  const acc = (id: string) => accounts.find((a) => a.id === id);
  const rev = project.revision;
  const currentReviews = project.reviews || [];

  return (
    <>
      <Panel>
        <PanelHead>
          {project.studio.title || projectId}
          <Tag size="sm">{rev.format}</Tag>
          <Grow />
          <Button size="sm" variant="secondary" onClick={load}>
            Refresh
          </Button>
        </PanelHead>
        <Row>
          <Small>Current revision</Small>
          <Mono>{project.studio.currentRevisionId}</Mono>
          <Small>
            Approvals: {project.approvals.byStatus.active || 0} active, {project.approvals.byStatus.stale || 0} stale
          </Small>
        </Row>
        <Row>
          {project.studio.targets.map((t) => (
            <Tag key={t.accountId} size="sm">
              @{acc(t.accountId)?.handle || t.accountId} via {modeLabel(t.deliveryMode)}
            </Tag>
          ))}
        </Row>
        {staleNotice ? (
          <Banner $tone="warning" data-testid="stale-notice">
            {staleNotice}
          </Banner>
        ) : null}
      </Panel>

      <Panel>
        <PanelHead>Revision history</PanelHead>
        {history.map((r, i) => (
          <ListRow key={r.id}>
            <Mono>{r.id}</Mono>
            {r.id === project.studio.currentRevisionId ? (
              <Tag size="sm" variant="accent">
                current
              </Tag>
            ) : null}
            <Small>{new Date(r.createdAt).toLocaleString()}</Small>
            <Small>Changed: {revisionDiff(history[i + 1] || null, r).join(", ")}</Small>
          </ListRow>
        ))}
      </Panel>

      {rev.renderSpec?.contentPlanning && (
        <Panel>
          <PanelHead>Prepared production brief</PanelHead>
          <Hint>This is the exact planning snapshot for this project revision. Update the card and prepare a new review to change it.</Hint>
          <details>
            <summary>View references and production prompts</summary>
            <ProductionPlanningSummary brief={rev.renderSpec.contentPlanning.productionBrief} references={rev.renderSpec.contentPlanning.references} />
          </details>
        </Panel>
      )}

      <ReviseForm
        communityId={communityId}
        project={project}
        accounts={accounts}
        onRevised={(res) => {
          const stale = (res.data?.staleApprovalIds || []) as string[];
          if (res.data?.newRevision) {
            setStaleNotice(
              stale.length > 0
                ? `Revision ${res.data.revisionId} created. ${stale.length} earlier approvals are stale and need a new review before anything is sent.`
                : `Revision ${res.data.revisionId} created. Earlier approvals do not carry over. Submit it for review.`
            );
          }
          load();
        }}
      />

      <Panel>
        <PanelHead>Render</PanelHead>
        <RenderControls communityId={communityId} projectId={projectId} revisionId={rev.id} onJob={setRenderJobId} />
        {renderJobId ? <JobProgress communityId={communityId} jobId={renderJobId} onTerminal={() => load()} /> : null}
      </Panel>

      <SubmitReview communityId={communityId} project={project} onSubmitted={(rid) => {
        setOpenReviewId(rid);
        load();
      }} />

      <Panel>
        <PanelHead>Reviews of this revision</PanelHead>
        {currentReviews.length === 0 ? <Small>No review yet.</Small> : null}
        {currentReviews.map((r) => (
          <ListRow key={r.reviewId} $active={openReviewId === r.reviewId}>
            <Mono>{r.reviewId}</Mono>
            <Tag size="sm" variant={r.status === "approved" ? "success" : r.status === "changes_requested" ? "warning" : "info"}>
              {r.status}
            </Tag>
            <Small>{Object.keys(r.approvals || {}).length} of {r.targets.length} approved</Small>
            <Grow />
            <Button size="sm" variant="secondary" onClick={() => setOpenReviewId(openReviewId === r.reviewId ? null : r.reviewId)}>
              {openReviewId === r.reviewId ? "Close" : "Open review"}
            </Button>
          </ListRow>
        ))}
        {openReviewId ? (
          <ReviewPanel
            communityId={communityId}
            reviewId={openReviewId}
            currentRevisionId={project.studio.currentRevisionId}
            staleNotice={staleNotice}
            onChanged={load}
          />
        ) : null}
      </Panel>

      <Panel>
        <PanelHead>Approved targets</PanelHead>
        <Hint>Only active approvals of the current revision can be scheduled.</Hint>
        {project.approvals.activeForRevision.length === 0 ? <Small>No active approvals for this revision.</Small> : null}
        {project.approvals.activeForRevision.map((ap) => {
          const a = acc(ap.accountId);
          return (
            <ListRow key={ap.approvalId}>
              <strong>@{a?.handle || ap.accountId}</strong>
              <Tag size="sm">{modeLabel(ap.deliveryMode)}</Tag>
              <Small>Owner: {a?.ownerMasked || "not set"}</Small>
              <Grow />
              <Button
                size="sm"
                disabled={!a}
                onClick={() =>
                  a &&
                  setScheduling({
                    accountId: a.id,
                    handle: a.handle,
                    timezone: a.timezone,
                    deliveryMode: ap.deliveryMode,
                    approvalId: ap.approvalId,
                  })
                }
              >
                Schedule
              </Button>
            </ListRow>
          );
        })}
        {scheduling ? (
          <ScheduleDialog
            communityId={communityId}
            projectId={projectId}
            revisionId={rev.id}
            target={scheduling}
            onClose={() => setScheduling(null)}
            onScheduled={load}
          />
        ) : null}
      </Panel>
    </>
  );
};

const ReviseForm: React.FC<{
  communityId: string;
  project: ProjectData;
  accounts: AccountSummary[];
  onRevised: (res: OperationResult) => void;
}> = ({ communityId, project, accounts, onRevised }) => {
  const rev = project.revision;
  const [open, setOpen] = useState(false);
  const [captions, setCaptions] = useState<Record<string, string>>(rev.captionsByAccount || {});
  const [order, setOrder] = useState<string[]>((rev.assets || []).map((a) => a.assetId));
  const m = useMutation();
  useEffect(() => {
    setCaptions(rev.captionsByAccount || {});
    setOrder((rev.assets || []).map((a) => a.assetId));
  }, [rev]);
  const move = (i: number, dir: -1 | 1) => {
    m.edited();
    setOrder((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = prev.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const submit = async () => {
    const patch: Record<string, unknown> = {};
    const changed: Record<string, string> = {};
    Object.keys(captions).forEach((k) => {
      if (captions[k] !== (rev.captionsByAccount || {})[k]) changed[k] = captions[k];
    });
    if (Object.keys(changed).length) patch.captionsByAccount = changed;
    if (order.join(",") !== (rev.assets || []).map((a) => a.assetId).join(",")) patch.sourceAssetIds = order;
    if (Object.keys(patch).length === 0) return;
    const res = await m.run((k) =>
      mediaOps.reviseProject(k, communityId, { projectId: project.projectId, baseRevisionId: rev.id, patch })
    );
    if (res && res.status !== "blocked") {
      setOpen(false);
      onRevised(res);
    }
  };
  if (!open) {
    return (
      <Panel>
        <PanelHead>
          Revise <Grow />
          <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
            Edit captions or order
          </Button>
        </PanelHead>
        <Hint>A revision is a new immutable version. Approvals of earlier revisions stop counting.</Hint>
        <ResultNotice result={m.result} okText="Revision created." />
      </Panel>
    );
  }
  return (
    <Panel>
      <PanelHead>Revise</PanelHead>
      <Banner $tone="warning">Saving creates a new revision. Every earlier approval becomes stale and needs a new review.</Banner>
      {Object.keys(captions).map((aid) => (
        <FieldLabel key={aid}>
          Caption for @{accounts.find((a) => a.id === aid)?.handle || aid}
          <TextArea
            value={captions[aid]}
            onChange={(e) => {
              const v = e.target.value;
              setCaptions((prev) => ({ ...prev, [aid]: v }));
              m.edited();
            }}
          />
        </FieldLabel>
      ))}
      {order.map((id, i) => (
        <ListRow key={id}>
          <Small>{i + 1}.</Small>
          <Mono>{id}</Mono>
          <Grow />
          <Button size="sm" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0}>
            Up
          </Button>
          <Button size="sm" variant="ghost" onClick={() => move(i, 1)} disabled={i === order.length - 1}>
            Down
          </Button>
        </ListRow>
      ))}
      <Row>
        <Button size="sm" onClick={submit} isLoading={m.busy}>
          Save new revision
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </Row>
      <ResultNotice result={m.result} okText="Revision created." />
    </Panel>
  );
};

const RenderControls: React.FC<{ communityId: string; projectId: string; revisionId: string; onJob: (id: string) => void }> = ({
  communityId,
  projectId,
  revisionId,
  onJob,
}) => {
  const [quality, setQuality] = useState<"draft" | "final">("draft");
  const [budget, setBudget] = useState("0");
  const m = useMutation<{ jobId?: string | null }>();
  const submit = async () => {
    const res = await m.run((k) =>
      mediaOps.startRender(k, communityId, { projectId, revisionId, quality, budgetLimitUsd: Number(budget) || 0 })
    );
    if (res?.data?.jobId) onJob(res.data.jobId);
  };
  return (
    <>
      <FormGrid>
        <FieldLabel>
          Quality
          <Select
            value={quality}
            onChange={(e) => {
              setQuality(e.target.value as "draft" | "final");
              m.edited();
            }}
          >
            <option value="draft">Draft</option>
            <option value="final">Final</option>
          </Select>
        </FieldLabel>
        <FieldLabel>
          Budget limit in USD (0 means no paid stage)
          <TextInput
            type="number"
            min={0}
            value={budget}
            onChange={(e) => {
              setBudget(e.target.value);
              m.edited();
            }}
          />
        </FieldLabel>
      </FormGrid>
      <Row>
        <Button size="sm" variant="secondary" onClick={submit} isLoading={m.busy}>
          Start render
        </Button>
      </Row>
      <ResultNotice result={m.result} />
    </>
  );
};

const SubmitReview: React.FC<{ communityId: string; project: ProjectData; onSubmitted: (reviewId: string) => void }> = ({
  communityId,
  project,
  onSubmitted,
}) => {
  const m = useMutation<{ reviewId?: string }>();
  const submit = async () => {
    const res = await m.run((k) =>
      mediaOps.submitReview(k, communityId, {
        projectId: project.projectId,
        revisionId: project.studio.currentRevisionId,
        targets: project.studio.targets.map((t) => ({ accountId: t.accountId, deliveryMode: t.deliveryMode })),
      })
    );
    if (res?.data?.reviewId) onSubmitted(res.data.reviewId);
  };
  return (
    <Panel>
      <PanelHead>
        Submit for review <Grow />
        <Button size="sm" onClick={submit} isLoading={m.busy} disabled={!project.isCurrent}>
          Submit current revision
        </Button>
      </PanelHead>
      <ResultNotice result={m.result} okText="Submitted for review." />
    </Panel>
  );
};

const ReviewPanel: React.FC<{
  communityId: string;
  reviewId: string;
  currentRevisionId: string;
  staleNotice: string;
  onChanged: () => void;
}> = ({ communityId, reviewId, currentRevisionId, staleNotice, onChanged }) => {
  const [review, setReview] = useState<ReviewView | null>(null);
  const [loadError, setLoadError] = useState<OperationResult | null>(null);
  const [feedback, setFeedback] = useState("");
  const approve = useMutation();
  const changes = useMutation();

  const load = useCallback(async () => {
    const r = await mediaOps.getReview(communityId, reviewId);
    if (r.status === "blocked") {
      setLoadError(r);
      return;
    }
    setLoadError(null);
    setReview(r.data);
  }, [communityId, reviewId]);
  useEffect(() => {
    load();
  }, [load]);

  if (!review) return loadError ? <ResultNotice result={loadError} /> : <Small>Loading review.</Small>;
  const outdated = review.revisionId !== currentRevisionId;
  const media = (review.media || []).slice().sort((a, b) => a.order - b.order);

  const doApprove = async (accountId: string) => {
    // One idempotency key per (approve, account): a retry for one account
    // never reuses another account's key.
    const res = await approve.run(
      (k) =>
        mediaOps.approve(k, communityId, { reviewId, revisionId: review.revisionId, expectedVersion: review.version, targetAccountIds: [accountId] }),
      `approve:${accountId}`
    );
    if (res && res.status !== "blocked") {
      await load();
      onChanged();
    }
  };
  const doChanges = async () => {
    const res = await changes.run((k) =>
      mediaOps.requestChanges(k, communityId, { reviewId, expectedVersion: review.version, feedback: feedback.trim() })
    );
    if (res && res.status !== "blocked") {
      setFeedback("");
      await load();
      onChanged();
    }
  };

  return (
    <Panel data-testid="review-panel">
      <PanelHead>
        Review <Mono>{reviewId}</Mono> <Tag size="sm">{review.status}</Tag>
      </PanelHead>
      {outdated ? (
        <Banner $tone="danger">This review is for an older revision. Its approvals are stale. Submit the current revision for review.</Banner>
      ) : null}
      {staleNotice && !outdated ? <Banner $tone="warning">{staleNotice}</Banner> : null}
      <div>
        <Small>Media in delivery order</Small>
        <Row>
          {media.map((x, i) => {
            const url = x.mediaUrl || null;
            return url && x.type !== "video" ? (
              <Thumb key={i} src={url} alt={`Item ${i + 1}`} />
            ) : url ? (
              <video key={i} src={url} style={{ width: 64, height: 80, objectFit: "cover", borderRadius: 6 }} muted />
            ) : (
              <ThumbBox key={i}>
                {i + 1}. {x.assetId || x.storagePath || "item"}
              </ThumbBox>
            );
          })}
        </Row>
      </div>
      {(review.targets || []).map((t) => {
        const findings = (review.findings || {})[t.accountId] || [];
        const approvedId = (review.approvals || {})[t.accountId];
        return (
          <ListRow key={t.accountId} style={{ flexDirection: "column", alignItems: "stretch" }}>
            <Row>
              <strong>@{t.handle || t.accountId}</strong>
              <Tag size="sm">{t.platform}</Tag>
              <Tag size="sm">{modeLabel(t.deliveryMode)}</Tag>
              <Small>Owner: {t.ownerMasked || "not set"}</Small>
              {approvedId ? (
                <Tag size="sm" variant="success">
                  Approved
                </Tag>
              ) : null}
            </Row>
            <Small>Caption delivered to this account</Small>
            <pre style={{ whiteSpace: "pre-wrap", margin: 0, fontFamily: "var(--font-sans)", fontSize: 13 }}>
              {t.caption ?? review.captionsByAccount?.[t.accountId] ?? "No caption for this account."}
            </pre>
            {findings.length > 0 ? (
              <Banner $tone="danger">
                {findings.map((f, i) => (
                  <ErrorLine key={i} error={{ ...f, retryable: false }} />
                ))}
              </Banner>
            ) : (
              <Small>No policy findings.</Small>
            )}
            <Row>
              <Button
                size="sm"
                onClick={() => doApprove(t.accountId)}
                disabled={outdated || !!approvedId || findings.length > 0}
                isLoading={approve.busy}
              >
                Approve for @{t.handle || t.accountId}
              </Button>
            </Row>
          </ListRow>
        );
      })}
      <ResultNotice result={approve.result} okText="Approved. This approval covers this exact revision, account, mode and owner." />
      <FieldLabel>
        Request changes
        <TextArea
          value={feedback}
          onChange={(e) => {
            setFeedback(e.target.value);
            changes.edited();
          }}
        />
      </FieldLabel>
      <Row>
        <Button size="sm" variant="secondary" onClick={doChanges} disabled={!feedback.trim()} isLoading={changes.busy}>
          Request changes
        </Button>
      </Row>
      <ResultNotice result={changes.result} okText="Changes requested. Approvals from this review no longer count." />
    </Panel>
  );
};

export default ProjectDetail;
